import React, { useEffect, useMemo, useState } from 'react';
import Meta from 'src/components/Meta';
import { useGaEventPageView } from 'src/utils/gaEvent';
import styled from 'styled-components';
import { AssessmentCardSkeleton, PageContainer, Poppins, Spacer } from 'src/common';
import { FrameworkLibraryCard } from './comps';
import { Link, useNavigate } from 'react-router-dom';
import { Tile, TileContainer, TileTitle } from 'src/components/Tile';
import { mpEvent } from 'src/utils/mixpanel/useMixPanel';
import { MPEvents } from 'src/utils/mixpanel/types';
import { useControlFrameworksListQuery, useWizardQuery } from 'src/graphql/_generated';
import { isQueryLoading } from 'src/graphql/client';
import { getOrderedFrameworks, reorderPinnedFrameworks, sanitizePinnedFrameworkIds } from './util';
import { updateWizard } from 'src/api/wizard';
import { useMutation } from 'react-query';
import { showErrorToast } from 'src/components/Toast';
import { useCache } from 'src/graphql';
import useActionsApp from 'src/redux/actionHooks/useActionsApp';
import { WizardIntent } from 'src/redux/reducers/app/types';
import { useAuth } from 'src/state/auth';
import Button from 'src/components/form/Button';
import { AddControlFrameworkModal } from './AddControlFrameworkModal';
import { EditControlFrameworkModal } from './EditControlFrameworkModal';
import { PublishControlFrameworkModal } from './PublishControlFrameworkModal';

const Div = styled(PageContainer)`
  .head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
  }

  .actions {
    display: flex;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;
    justify-content: flex-end;
  }

  .panel {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    grid-gap: 15px;
  }

  .error {
    color: ${({ theme }) => theme.colors.error};
    font-size: 14px;
  }
  .success {
    color: ${({ theme }) => theme.colors.success};
    font-size: 14px;
  }

  ${({ theme }) => theme.breakpoints.down('sm')} {
    .head {
      flex-direction: column;
      align-items: flex-start;
    }

    .actions {
      width: 100%;
      justify-content: flex-start;
    }
  }
`;

const FrameworkLibrariesPage = () => {
  const navigate = useNavigate();
  useGaEventPageView('Cydea | Control Frameworks');
  const { refetchQuery } = useCache();
  const { setWizardWorkspace } = useActionsApp();
  const { isPlatformAdmin } = useAuth();
  const [isAddFrameworkOpen, setIsAddFrameworkOpen] = useState(false);
  const [isEditFrameworkOpen, setIsEditFrameworkOpen] = useState(false);
  const [isPublishFrameworkOpen, setIsPublishFrameworkOpen] = useState(false);

  const { data, loading, networkStatus, refetch } = useControlFrameworksListQuery();
  const { data: wizardData } = useWizardQuery();
  const isLoading = isQueryLoading({ loading, data, networkStatus });
  const frameworks = data?.controlFrameworks || [];
  const primaryFrameworkId = wizardData?.profile?.activeWorkspace?.questions?.regulations?.primaryControlFramework;
  const wizardRegulations = wizardData?.profile?.activeWorkspace?.questions?.regulations;
  const derivedPinnedFrameworkIds = useMemo(
    () =>
      sanitizePinnedFrameworkIds({
        frameworks,
        primaryFrameworkId,
        pinnedFrameworkIds: wizardRegulations?.pinnedControlFrameworks,
      }),
    [frameworks, primaryFrameworkId, wizardRegulations?.pinnedControlFrameworks],
  );
  const [pinnedFrameworkIds, setPinnedFrameworkIds] = useState<string[]>(derivedPinnedFrameworkIds);
  const [draggingFrameworkId, setDraggingFrameworkId] = useState<string | null>(null);
  const [updateWizardData] = useMutation(updateWizard);

  useEffect(() => {
    setPinnedFrameworkIds(derivedPinnedFrameworkIds);
  }, [derivedPinnedFrameworkIds]);

  const orderedFrameworks = useMemo(
    () =>
      getOrderedFrameworks({
        frameworks,
        primaryFrameworkId,
        pinnedFrameworkIds,
      }),
    [frameworks, pinnedFrameworkIds, primaryFrameworkId],
  );

  const persistPinnedFrameworks = async (nextPinnedFrameworkIds: string[], previousPinnedFrameworkIds: string[]) => {
    if (!wizardRegulations) {
      setPinnedFrameworkIds(previousPinnedFrameworkIds);
      showErrorToast('Workspace framework preferences are not available yet.');
      return;
    }

    setPinnedFrameworkIds(nextPinnedFrameworkIds);

    try {
      await updateWizardData({
        data: {
          regulations: {
            legislation: wizardRegulations.legislation || [],
            controlFrameworks: wizardRegulations.controlFrameworks || [],
            controlFrameworksOther: wizardRegulations.controlFrameworksOther || [],
            primaryControlFramework: wizardRegulations.primaryControlFramework || undefined,
            primaryFrameworkControls: wizardRegulations.primaryFrameworkControls || [],
            pinnedControlFrameworks: nextPinnedFrameworkIds,
          },
        },
      });
      await refetchQuery({ include: ['wizard'] });
    } catch (error: any) {
      setPinnedFrameworkIds(previousPinnedFrameworkIds);
      showErrorToast(error?.message || 'Unable to update pinned frameworks.');
    }
  };

  const onPinToggle = (frameworkId: string) => {
    if (frameworkId === primaryFrameworkId) return;

    const previousPinnedFrameworkIds = [...pinnedFrameworkIds];
    const isPinned = pinnedFrameworkIds.includes(frameworkId);
    const nextPinnedFrameworkIds = isPinned
      ? pinnedFrameworkIds.filter((id) => id !== frameworkId)
      : sanitizePinnedFrameworkIds({
          frameworks,
          primaryFrameworkId,
          pinnedFrameworkIds: [...pinnedFrameworkIds, frameworkId],
        });

    if (!isPinned && nextPinnedFrameworkIds.length === pinnedFrameworkIds.length) {
      showErrorToast('You can pin up to three frameworks, including the primary framework.');
      return;
    }

    void persistPinnedFrameworks(nextPinnedFrameworkIds, previousPinnedFrameworkIds);
  };

  const onPinnedFrameworkDrop = (targetFrameworkId: string) => {
    if (!draggingFrameworkId || draggingFrameworkId === targetFrameworkId) return;

    const previousPinnedFrameworkIds = [...pinnedFrameworkIds];
    const nextPinnedFrameworkIds = reorderPinnedFrameworks({
      pinnedFrameworkIds,
      sourceFrameworkId: draggingFrameworkId,
      targetFrameworkId,
    });

    if (nextPinnedFrameworkIds === pinnedFrameworkIds) return;

    setDraggingFrameworkId(null);
    void persistPinnedFrameworks(nextPinnedFrameworkIds, previousPinnedFrameworkIds);
  };

  return (
    <>
      <Meta title={`Cydea | Control Frameworks`} feedbackScript />
      <Div>
        <div className="head">
          <Poppins px={32} weight={600}>
            Control Frameworks
          </Poppins>
          {isPlatformAdmin && (
            <div className="actions">
              <Button
                secondary
                $constWidth={230}
                onClick={() => setIsEditFrameworkOpen(true)}
                data-cy="open-edit-control-framework-modal"
              >
                Edit Control Framework
              </Button>
              <Button
                secondary
                $constWidth={250}
                onClick={() => setIsPublishFrameworkOpen(true)}
                data-cy="open-publish-control-framework-modal"
              >
                Publish Control Framework
              </Button>
              <Button
                primary
                $constWidth={230}
                onClick={() => setIsAddFrameworkOpen(true)}
                data-cy="open-add-control-framework-modal"
              >
                Add Control Framework
              </Button>
            </div>
          )}
        </div>
        <Spacer $px={20} />
        <Poppins px={14} color="cflowerBlue">
          Primary and pinned frameworks stay at the top for this workspace. Drag pinned cards to reorder them.
        </Poppins>
        <Spacer $px={20} />
        <TileContainer>
          {!isLoading ? (
            <>
              {orderedFrameworks.map((el) => (
                <FrameworkLibraryCard
                  key={el.id}
                  framework={el}
                  isPrimary={el.id === primaryFrameworkId}
                  isPinned={pinnedFrameworkIds.includes(el.id)}
                  isDragEnabled={pinnedFrameworkIds.length > 1 && pinnedFrameworkIds.includes(el.id)}
                  onTogglePin={() => onPinToggle(el.id)}
                  onChangePrimary={() => setWizardWorkspace({ intent: { type: WizardIntent.framework } })}
                  onDragStart={() => setDraggingFrameworkId(el.id)}
                  onDragEnd={() => setDraggingFrameworkId(null)}
                  onDragOver={(event) => {
                    if (pinnedFrameworkIds.includes(el.id)) {
                      event.preventDefault();
                    }
                  }}
                  onDrop={(event) => {
                    event.preventDefault();
                    onPinnedFrameworkDrop(el.id);
                  }}
                />
              ))}
              <Tile
                className="hover-shadow"
                data-cy="all-controls-card"
                onClick={() => {
                  navigate('/control-frameworks/all-controls');
                  mpEvent(MPEvents.Link, {
                    source: {
                      value: ['Frameworks'],
                    },
                    destination: { value: ['Framework Controls all'] },
                    tags: ['FRAMEWORK'],
                  });
                }}
              >
                <Link
                  to="/control-frameworks/all-controls"
                  onClick={(e) => {
                    e.stopPropagation();
                    mpEvent(MPEvents.Link, {
                      source: {
                        value: ['Frameworks'],
                      },
                      destination: { value: ['Framework Controls all'] },
                      tags: ['FRAMEWORK'],
                    });
                  }}
                >
                  <TileTitle>View All Controls</TileTitle>
                </Link>
              </Tile>
            </>
          ) : (
            <>
              <AssessmentCardSkeleton />
              <AssessmentCardSkeleton />
              <AssessmentCardSkeleton />
            </>
          )}
        </TileContainer>
        {isPlatformAdmin && isAddFrameworkOpen && (
          <AddControlFrameworkModal
            isOpen={isAddFrameworkOpen}
            onClose={() => setIsAddFrameworkOpen(false)}
            onCreated={async () => {
              await refetch();
            }}
          />
        )}
        {isPlatformAdmin && isEditFrameworkOpen && (
          <EditControlFrameworkModal
            isOpen={isEditFrameworkOpen}
            onClose={() => setIsEditFrameworkOpen(false)}
            frameworks={frameworks}
            onEdited={async () => {
              await refetch();
            }}
          />
        )}
        {isPlatformAdmin && isPublishFrameworkOpen && (
          <PublishControlFrameworkModal
            isOpen={isPublishFrameworkOpen}
            onClose={() => setIsPublishFrameworkOpen(false)}
            frameworks={frameworks}
            onPublished={async () => {
              await refetch();
            }}
          />
        )}
      </Div>
    </>
  );
};

export default FrameworkLibrariesPage;
